import isURL from 'validator/lib/isURL';

import type { TargetDomainEntry } from '@/lib/types/tenant';

/** The customer-facing host: BYOD hostnames rewrite Host, so the public one rides x-original-host. */
export function publicRequestHost(source: Request | Headers): string | null {
  // Not `'headers' in source`: Next's `headers()` adapter has a raw `headers` field of its own.
  const headers = source instanceof Request ? source.headers : source;
  return headers.get('x-original-host') ?? headers.get('host');
}

// Raw Host, not x-original-host: the preview router serves a ticket env on clean names and leaves
// the ticket host here.
export function ticketRoutingHost(source: Request | Headers): string | null {
  return (source instanceof Request ? source.headers : source).get('host');
}

/**
 * Resolves the public-facing base URL from a request, accounting for reverse proxies.
 *
 * Priority: x-original-host → x-forwarded-host → host header → request URL origin.
 */
export function getBaseUrl(request: Request): string {
  const originalHost = request.headers.get('x-original-host');
  const forwardedHost = request.headers.get('x-forwarded-host');
  const hostHeader = request.headers.get('host');
  const forwardedProto = request.headers.get('x-forwarded-proto') || 'https';

  const host = originalHost || forwardedHost || hostHeader;
  if (host) {
    return `${forwardedProto}://${host}`;
  }
  return new URL(request.url).origin;
}

/**
 * The tenant's own `*.bazak.ai` origin, even when reached through its BYOD hostname: the Gateway
 * rewrites `Host` to it. For URLs we publish, which must not hand out the customer's hostname.
 * The scheme follows the host, not `x-forwarded-proto`: that header is client-supplied unless a
 * proxy overwrites it, and every deployed host is HTTPS-only.
 */
export function getTenantHostOrigin(request: Request): string {
  const host = request.headers.get('host');
  if (!host) return new URL(request.url).origin;
  return `${isLocalhost(host.split(':')[0]) ? 'http' : 'https'}://${host}`;
}

export function getHostname(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

export function stripWww(hostname: string): string {
  return hostname.startsWith('www.') ? hostname.slice(4) : hostname;
}

export function hostnamesMatch({ a, b }: { a: string; b: string }): boolean {
  return stripWww(a) === stripWww(b);
}

export function isLocalhost(hostname: string): boolean {
  return hostname === 'localhost' || hostname.endsWith('.localhost');
}

export function matchesDomain({
  hostname,
  pattern,
}: {
  hostname: string;
  pattern: string;
}): boolean {
  if (pattern.startsWith('*.')) {
    const baseDomain = pattern.slice(2);
    return hostname === baseDomain || hostname.endsWith(`.${baseDomain}`);
  }
  return hostname === pattern || hostname.endsWith(`.${pattern}`);
}

export function getDomainPattern(entry: TargetDomainEntry): string {
  return typeof entry === 'string' ? entry : entry.pattern;
}

/**
 * Returns the first entry whose pattern matches `hostname`. Order matters —
 * callers should list more specific patterns before wildcards.
 */
export function findMatchingDomainEntry({
  hostname,
  entries,
}: {
  hostname: string;
  entries: readonly TargetDomainEntry[];
}): TargetDomainEntry | null {
  for (const entry of entries) {
    if (matchesDomain({ hostname, pattern: getDomainPattern(entry) })) return entry;
  }
  return null;
}

export function urlMatchesDomains({
  href,
  domains,
}: {
  href: string;
  domains: readonly TargetDomainEntry[];
}): boolean {
  const hostname = getHostname(href);
  if (!hostname) return false;
  return findMatchingDomainEntry({ hostname, entries: domains }) !== null;
}

export function isValidHttpUrl(href: string): boolean {
  return isURL(href, { require_protocol: true, protocols: ['http', 'https'] });
}

/** `isValidHttpUrl` for tool-result fields, which can arrive as any type at runtime; `isURL` throws on a non-string. */
export function safeHttpUrl(value: unknown): string | undefined {
  return typeof value === 'string' && isValidHttpUrl(value) ? value : undefined;
}

export function matchesUrlPattern({ url, pattern }: { url: string; pattern: string }): boolean {
  const regexPattern = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp(regexPattern).test(url);
}

export function matchesAnyPattern({
  url,
  patterns,
}: {
  url: string;
  patterns: string | string[];
}): boolean {
  const patternList = Array.isArray(patterns) ? patterns : [patterns];
  return patternList.some(pattern => matchesUrlPattern({ url, pattern }));
}

export function applyQueryParams({
  url,
  params,
}: {
  url: string;
  params: Record<string, string>;
}): string {
  try {
    const urlObj = new URL(url);
    Object.entries(params).forEach(([key, value]) => {
      urlObj.searchParams.set(key, value);
    });
    return urlObj.toString();
  } catch {
    return url;
  }
}

export function interpolateTemplate({
  template,
  tokens,
}: {
  template: string;
  tokens: Record<string, string>;
}): string {
  return template.replace(/\{([\w-]+)\}/g, (match, key) => tokens[key] ?? match);
}

/** Normalize HTTP(S) origins; paths, query strings and fragments are not origins. */
// Keep the origin contract aligned with third-party-gw-api website-config.ts (httpOriginSchema).
export function parseHttpOrigin(raw?: string): string | undefined {
  if (!raw) return undefined;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return undefined;
  }
  if (
    !['http:', 'https:'].includes(parsed.protocol) ||
    parsed.username ||
    parsed.password ||
    parsed.pathname !== '/' ||
    parsed.search ||
    parsed.hash
  )
    return undefined;
  return parsed.origin;
}
